import React, { useRef, useEffect } from 'react';

interface SnowfallProps {
  imageSrc: string;
  windSpeed?: number;
  windDirection?: number;
  particleCount?: number;
}

const Snowfall: React.FC<SnowfallProps> = ({
  imageSrc,
  windSpeed = 0,
  windDirection = 0,
  particleCount = 17,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas: any = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    if (!context) return;

    const particles: Particle[] = [];
    const img = new Image();
    img.src = imageSrc;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const radian = (windDirection * Math.PI) / 180;

    class Particle {
      x: number;
      y: number;
      speed: number;
      size: number;
      rotation: number;
      rotationSpeed: number;
      xSpeed: number;
      ySpeed: number;

      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.speed = Math.random() * 1 + 1;
        this.size = Math.random() * 70 + 5;
        this.rotation = Math.random() * 360;
        this.rotationSpeed = (Math.random() - 0.5) * 2;
        this.xSpeed = windSpeed * Math.cos(radian);
        this.ySpeed = windSpeed * Math.sin(radian);
      }

      update() {
        this.y += this.speed + this.ySpeed;
        this.x += this.xSpeed;
        this.rotation += this.rotationSpeed;

        if (this.y > canvas.height) {
          this.y = -this.size;
          this.x = Math.random() * canvas.width;
        }
        if (this.x > canvas.width) {
          this.x = 0;
        } else if (this.x < 0) {
          this.x = canvas.width;
        }
      }

      draw() {
        context.save();
        context.translate(this.x, this.y);
        context.rotate((this.rotation * Math.PI) / 180);
        context.drawImage(img, -this.size / 2, -this.size / 2, this.size, this.size);
        context.restore();
      }
    }

    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }

    const animate = () => {
      context.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(particle => {
        particle.update();
        particle.draw();
      });
      requestAnimationFrame(animate);
    };

    img.onload = animate;

    const handleResize = () => {
      if (canvas) {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [imageSrc, windSpeed, windDirection, particleCount]);

  return <canvas ref={canvasRef} style={{ display: 'block' }} />;
};

export default Snowfall;
